// connect/memory.ts — what a connection remembers about the thing on the other
// end of it.
//
// A connection row already knew WHO (the name the person gave it) and WHEN (its
// first and last call). It did not know WHAT: which client, which version, on
// which kind of machine, through which door, signed in as which registered
// app. So every time a tool came back — a re-install, an editor that registers
// a fresh OAuth client on every sign-in (Antigravity registered three in ten
// seconds on 2026-09-27), the bare link pasted instead of the tagged one — the
// server had nothing to recognise it by, and minted a second row for the same
// relationship.
//
// The memory is that recognition. It is written by the server only (the MCP
// route, migration 122's `connection_remember` / `connection_arrive`), it never
// holds a secret or a word of the person's content — only what the client says
// about itself — and it is what lets the next arrival of the same tool on the
// same machine land on the connection it already had.
//
// Pure module — no I/O. The route builds a patch with `memoryFromRequest`; the
// page reads it back with `memoryLine`.

/** What a connection keeps about its client. Every field is optional: a client
 *  says as much or as little about itself as it likes. */
export interface ConnectionMemory {
  /** the MCP client, as it names itself (initialize clientInfo, or `_meta`). */
  client?: { name: string; version?: string }
  /** the protocol revision it last spoke. */
  protocol?: string
  /** the OAuth app it registered as, and where its sign-in lands. */
  oauth?: { name?: string; redirect?: string }
  /** the machine, as far as the request can tell. */
  device?: { os?: DeviceOs; kind?: DeviceKind }
  /** a short stable fingerprint of the machine's user-agent, versions worn off —
   *  with tool_id, how a re-registration of the same tool on the same machine
   *  is recognised. Never shown. */
  device_key?: string
  /** how the last call reached us: the connection's own link, or the bare one. */
  door?: 'tagged' | 'bare'
  /** the last user-agent, cut short — kept so a fingerprint can be re-derived. */
  ua?: string
  /** when the server last wrote any of this. */
  remembered_at?: string
}

export type DeviceOs = 'macos' | 'windows' | 'linux' | 'ios' | 'android'
export type DeviceKind = 'desktop' | 'web' | 'terminal' | 'mobile' | 'server'

export const OS_WORD: Record<DeviceOs, string> = {
  macos: 'mac',
  windows: 'windows',
  linux: 'linux',
  ios: 'iphone',
  android: 'android',
}

/** Read the operating system off a user-agent. Node clients rarely say, so
 *  null is the common, honest answer. */
export function osFromUA(ua: string | null | undefined): DeviceOs | undefined {
  if (!ua) return undefined
  const s = ua.toLowerCase()
  if (/iphone|ipad|ios\b/.test(s)) return 'ios'
  if (s.includes('android')) return 'android'
  if (/mac os|macintosh|darwin/.test(s)) return 'macos'
  if (/windows|win32|win64/.test(s)) return 'windows'
  if (/linux|x11|ubuntu/.test(s)) return 'linux'
  return undefined
}

/** What kind of place the call came from. A browser UA from one of the hosted
 *  assistants is `web` (claude.ai, chatgpt.com call from their own servers). */
export function kindFromUA(ua: string | null | undefined): DeviceKind | undefined {
  if (!ua) return undefined
  const s = ua.toLowerCase()
  if (/iphone|android|mobile/.test(s)) return 'mobile'
  if (/electron|cursor|antigravity|windsurf|vscode|code\/|zed/.test(s)) return 'desktop'
  if (/python|go-http|axios|undici|node|curl|innernet-cli|codex|gemini-cli|claude-code/.test(s)) return 'terminal'
  if (/openai-mcp|chatgpt|claude-user|anthropic|notion/.test(s)) return 'server'
  if (s.includes('mozilla')) return 'web'
  return undefined
}

/** FNV-1a, 32-bit, hex. Browser-safe (this module is read by the page too) and
 *  only ever a fingerprint — nothing here needs to resist an adversary, because
 *  the key grants nothing: it only decides which of the person's OWN rows an
 *  already-authorized call is attributed to. */
export function fingerprint(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/** The version-free shape of a user-agent: `antigravity/1.12.3 (darwin)` and
 *  `antigravity/1.13.0 (darwin)` are the same machine after an update. */
export function uaShape(ua: string): string {
  return ua.toLowerCase().replace(/\d+(\.\d+)+/g, '#').replace(/\s+/g, ' ').trim()
}

const clip = (s: unknown, n: number): string | undefined =>
  typeof s === 'string' && s.trim() ? s.trim().slice(0, n) : undefined

/** The redirect's origin, never its path or query (which can carry a port or a
 *  state we have no reason to keep). */
export function redirectOrigin(uri: string | null | undefined): string | undefined {
  if (!uri) return undefined
  try {
    const u = new URL(uri)
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.origin : `${u.protocol}//`
  } catch {
    return undefined
  }
}

/** Build the patch one request contributes. Only what the request actually
 *  said is included, so merging it never blanks something learned earlier. */
export function memoryFromRequest(input: {
  userAgent?: string | null
  clientInfo?: Record<string, unknown> | null
  protocolVersion?: string | null
  tagged: boolean
  oauthName?: string | null
  redirectUri?: string | null
  now?: Date
}): ConnectionMemory {
  const m: ConnectionMemory = { door: input.tagged ? 'tagged' : 'bare' }
  const name = clip(input.clientInfo?.name, 60)
  if (name) m.client = { name, ...(clip(input.clientInfo?.version, 30) ? { version: clip(input.clientInfo?.version, 30) } : {}) }
  const proto = clip(input.protocolVersion, 20)
  if (proto) m.protocol = proto
  const ua = clip(input.userAgent, 160)
  if (ua) {
    m.ua = ua
    const os = osFromUA(ua)
    const kind = kindFromUA(ua)
    if (os || kind) m.device = { ...(os ? { os } : {}), ...(kind ? { kind } : {}) }
  }
  const oauthName = clip(input.oauthName, 80)
  const redirect = redirectOrigin(input.redirectUri)
  if (oauthName || redirect) m.oauth = { ...(oauthName ? { name: oauthName } : {}), ...(redirect ? { redirect } : {}) }
  // the fingerprint is the machine's user-agent with its versions worn off —
  // nothing else, so the arrival (which knows the oauth app) and every later
  // call (which does not) derive the SAME key. The tool is scoped in SQL, by
  // tool_id. No user-agent, no key: it would match every client alike.
  if (ua) m.device_key = fingerprint(uaShape(ua))
  m.remembered_at = (input.now ?? new Date()).toISOString()
  return m
}

/** Read a stored memory defensively — it is jsonb, and older rows hold `{}`. */
export function readMemory(raw: unknown): ConnectionMemory {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as ConnectionMemory) : {}
}

/** The quiet line the connections page shows under a connection: what it is,
 *  where it runs, how it got in. Null when nothing has been learned yet. */
export function memoryLine(raw: unknown): string | null {
  const m = readMemory(raw)
  const parts: string[] = []
  if (m.client?.name) parts.push(m.client.version ? `${m.client.name.toLowerCase()} ${m.client.version}` : m.client.name.toLowerCase())
  if (m.device?.os) parts.push(`on ${OS_WORD[m.device.os]}`)
  if (m.door === 'bare') parts.push('through the plain link')
  return parts.length ? parts.join(' · ') : null
}
